import { useState, useEffect } from "react"
import { X, Search } from "lucide-react"
import questionData from "./questionLibraryData.json"

// ── Difficulty badge ──────────────────────────────────────────────────────────
const DifficultyBadge = ({ difficulty }) => {
  const styles = {
    Easy: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Medium: "bg-amber-100 text-amber-700 border-amber-200",
    Hard: "bg-rose-100 text-rose-700 border-rose-200",
  }
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide border ${styles[difficulty] ?? "bg-zinc-100 text-zinc-500 border-zinc-200"}`}
      style={{ borderRadius: 0 }}
    >
      {difficulty}
    </span>
  )
}

// ── Category tab pill ─────────────────────────────────────────────────────────
const CategoryTab = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border transition-all whitespace-nowrap ${
      active
        ? "bg-zinc-900 border-zinc-900 text-white"
        : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400 hover:text-zinc-900"
    }`}
    style={{ borderRadius: 0 }}
  >
    {label}
  </button>
)

// ── Question card (right grid) ────────────────────────────────────────────────
const QuestionCard = ({ question, selected, onClick }) => (
  <div
    className={`flex flex-col gap-2 p-3.5 border cursor-pointer transition-all group ${
      selected
        ? "border-zinc-900 bg-zinc-50"
        : "border-zinc-200 bg-white hover:border-zinc-400"
    }`}
    style={{ borderRadius: 0 }}
    onClick={() => onClick(question)}
  >
    <div className="flex items-center gap-1.5 flex-wrap">
      <DifficultyBadge difficulty={question.difficulty} />
      <span
        className="text-[9px] font-semibold uppercase tracking-wide text-zinc-400"
        style={{ fontFamily: "\'IBM Plex Mono\', monospace" }}
      >
        {question.topic}
      </span>
    </div>
    <p
      className={`text-xs font-bold leading-snug transition-colors ${selected ? "text-zinc-900" : "text-zinc-800 group-hover:text-zinc-900"}`}
    >
      {question.title}
    </p>
  </div>
)

// ── Preview panel (left column) ───────────────────────────────────────────────
const PreviewPanel = ({ question }) => {
  if (!question) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-3">
        <div className="w-8 h-8 border border-zinc-200 flex items-center justify-center">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-zinc-300"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-300">
          Click a question to preview
        </p>
      </div>
    )
  }

  const p = question.preview
  return (
    <div className="flex flex-col gap-5 p-5 overflow-y-auto hide-scroll h-full">
      <div className="flex flex-col gap-2 pb-4 border-b border-zinc-100">
        <div className="flex items-center gap-1.5">
          <DifficultyBadge difficulty={question.difficulty} />
          <span
            className="text-[9px] font-semibold uppercase tracking-wide text-zinc-400"
            style={{ fontFamily: "\'IBM Plex Mono\', monospace" }}
          >
            {question.topic}
          </span>
        </div>
        <h3 className="text-sm font-bold text-zinc-900 leading-snug">
          {question.title}
        </h3>
      </div>
      <Section title="Problem Statement">
        <p className="text-[13px] text-zinc-600 leading-relaxed">
          {p.problemStatement}
        </p>
      </Section>
      <Section title="Input Format">
        <p className="text-[13px] text-zinc-600 leading-relaxed">
          {p.inputFormat}
        </p>
      </Section>
      <Section title="Output Format">
        <p className="text-[13px] text-zinc-600 leading-relaxed">
          {p.outputFormat}
        </p>
      </Section>
      {p.example && (
        <Section title="Example / Notes">
          <pre
            className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-100 p-3 leading-relaxed whitespace-pre-wrap"
            style={{
              fontFamily: "\'IBM Plex Mono\', monospace",
              borderRadius: 0,
            }}
          >
            {p.example}
          </pre>
        </Section>
      )}
    </div>
  )
}

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
      {title}
    </span>
    {children}
  </div>
)

// ── Main QuestionLibrary modal ────────────────────────────────────────────────
const QuestionLibrary = ({ onClose, onAddQuestions }) => {
  const categories = ["Coding", "Case Study", "System Design", "Role Play"]
  const [activeCategory, setActiveCategory] = useState("Coding")
  const [searchQuery, setSearchQuery] = useState("")
  const [previewQuestion, setPreviewQuestion] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  // Track which category each selected question belongs to
  const [selectedCategories, setSelectedCategories] = useState({})

  const allQuestions = questionData[activeCategory] ?? []

  const filtered = allQuestions.filter((q) => {
    if (!searchQuery.trim()) return true
    const q_ = searchQuery.toLowerCase()
    return (
      q.title.toLowerCase().includes(q_) ||
      q.topic.toLowerCase().includes(q_) ||
      q.difficulty.toLowerCase().includes(q_)
    )
  })

  useEffect(() => {
    setPreviewQuestion(null)
  }, [activeCategory])

  const handleCardClick = (question) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(question.id) ? next.delete(question.id) : next.add(question.id)
      return next
    })
    // Track the category this question was selected from
    setSelectedCategories((prev) => ({
      ...prev,
      [question.id]: activeCategory,
    }))
    setPreviewQuestion(question)
  }

  const handleAdd = () => {
    const chosen = Object.values(questionData)
      .flat()
      .filter((q) => selectedIds.has(q.id))
      // Stamp each question with the category it was selected from
      .map((q) => ({ ...q, category: selectedCategories[q.id] ?? null }))
    onAddQuestions?.(chosen)
  }

  const hasSelection = selectedIds.size > 0

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-6"
        style={{ fontFamily: "\'IBM Plex Sans\', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 flex flex-col shadow-2xl"
          style={{
            borderRadius: 0,
            width: "min(960px, 95vw)",
            height: "min(680px, 90vh)",
          }}
        >
          <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-200 shrink-0">
            <div>
              <p className="text-sm font-bold text-zinc-900">
                Question Library
              </p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Select questions to add to your interview
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
              style={{ borderRadius: 0 }}
            >
              <X size={15} />
            </button>
          </div>

          <div className="flex items-center gap-3 px-6 py-3 border-b border-zinc-100 shrink-0 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search by skill, difficulty or question…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-[12px] border border-zinc-200 bg-white text-zinc-800 placeholder-zinc-300 focus:outline-none focus:border-zinc-400 transition-colors"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {categories.map((cat) => (
                <CategoryTab
                  key={cat}
                  label={cat}
                  active={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            <div
              className="border-r border-zinc-200 bg-white overflow-hidden"
              style={{ width: "38%" }}
            >
              <div className="px-5 py-3 border-b border-zinc-100 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Question Preview
                </span>
              </div>
              <div className="h-full overflow-y-auto hide-scroll">
                <PreviewPanel question={previewQuestion} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto hide-scroll p-4">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                    No questions found
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5">
                  {filtered.map((q) => (
                    <QuestionCard
                      key={q.id}
                      question={q}
                      selected={selectedIds.has(q.id)}
                      onClick={handleCardClick}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end px-6 py-3.5 border-t border-zinc-200 bg-white shrink-0">
            <button
              disabled={!hasSelection}
              onClick={handleAdd}
              className={`flex items-center gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-widest border transition-all ${
                hasSelection
                  ? "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-800"
                  : "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0 }}
            >
              Add to Interview Flow
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default QuestionLibrary
